import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Menu, X, User, LogOut, Settings, ChevronDown, MoreVertical, Users, LayoutDashboard, Radio, Bell, ChevronLeft, Search, Film, DollarSign, Rss, Calendar, CalendarDays, Award, GraduationCap, FileDown, Globe, Newspaper, Inbox, CreditCard } from 'lucide-react';
import EventHQLogo from '../../../assets/event-hq-logo.png';
import PNDiamondLogo from '../../../assets/PNdiamond.svg';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';
import { RoleSwitcher, RoleSwitcherCompact } from './RoleSwitcher';
import { usePlatform } from '../../hooks/usePlatform';
import { useMobileAppShell } from '../../hooks/useIsMobile';
import { useLiveStreamCount } from '../../hooks/useViewerPresence';
import { supabase } from '../../lib/supabase';
import { generateMonetizationDoc } from '../../lib/generateMonetizationDoc';
import { buildMyTeamsMenuItems, navigateToMyTeam } from '../../lib/myTeamsMenu';
import { NotificationBell } from '../notifications/NotificationBell';

interface MyEvent {
  id: string;
  name: string;
  slug: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
}

const PODIUM_ECOSYSTEM_LINKS = [
  { href: 'https://www.podiumnation.com', label: 'Podium Nation Custom Uniforms' },
  { href: 'https://www.thepodiumlab.com', label: 'Podium Lab - Fitness App' },
  { href: 'https://www.growyoursport.com', label: 'Coaching Development Community' },
  { href: 'https://www.wrestlingstudiopro.com', label: 'Wrestling Video Recording Software' },
  { href: 'https://www.quadmeet.ca', label: 'Offline Round Robin Software for Wrestling' },
] as const;

export function Header() {
  const { user, profile, signOut, isCoach } = useAuth();
  const { isViewingAsOrganizer, availableTeams, availableAthleteTeams, setActiveContext } = useRole();
  const { isNative } = usePlatform();
  const mobileAppShell = useMobileAppShell();
  const { count: liveStreamCount } = useLiveStreamCount();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [myTeamsOpen, setMyTeamsOpen] = useState(false);
  const [myEventsOpen, setMyEventsOpen] = useState(false);
  const [podiumEcosystemOpen, setPodiumEcosystemOpen] = useState(true);
  const [myEvents, setMyEvents] = useState<MyEvent[]>([]);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) {
      setMyEvents([]);
      return;
    }

    const fetchMyEvents = async () => {
      const { data: ownedOrgs } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id);

      const { data: memberOrgs } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id);

      const orgIds = new Set<string>();
      ownedOrgs?.forEach(org => orgIds.add(org.id));
      memberOrgs?.forEach(org => orgIds.add(org.organization_id));

      if (orgIds.size === 0) {
        setMyEvents([]);
        return;
      }

      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 14);
      const cutoffISO = cutoffDate.toISOString();

      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, slug, start_date, end_date, status')
        .in('organization_id', Array.from(orgIds))
        .neq('status', 'cancelled')
        .or(`end_date.is.null,end_date.gt.${cutoffISO}`)
        .order('start_date', { ascending: true });

      if (!error && data) {
        setMyEvents(data);
      }
    };

    fetchMyEvents();
  }, [user]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (userMenuOpen) {
      setPodiumEcosystemOpen(true);
    }
  }, [userMenuOpen]);

  const myTeamsMenuItems = useMemo(
    () => buildMyTeamsMenuItems(availableTeams, availableAthleteTeams),
    [availableTeams, availableAthleteTeams],
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setUserMenuOpen(false);
  };

  const handleMyTeamClick = (team: (typeof myTeamsMenuItems)[number]) => {
    navigateToMyTeam(team, setActiveContext, navigate);
    setUserMenuOpen(false);
    setMyTeamsOpen(false);
    setMobileMenuOpen(false);
  };

  // Compact app header (Capacitor + installed PWA only — not mobile Safari/Chrome)
  if (mobileAppShell) {
    return (
      <header className="mobile-header">
        <div className="mobile-header-inner">
          <Link to="/" className="flex items-center gap-2">
            <img src={EventHQLogo} alt="Event HQ" className="w-9 h-9 rounded-xl object-contain" />
            <span className="text-lg font-bold text-gray-900 dark:text-slate-100">Podium HQ</span>
          </Link>
          
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <NotificationBell variant="light" />
                <Link
                  to="/communications"
                  className="mobile-icon-btn"
                  title="My Inbox"
                  aria-label="My Inbox"
                >
                  <Inbox className="w-5 h-5 text-gray-600 dark:text-slate-300" />
                </Link>
                <Link
                  to="/dashboard"
                  className="mobile-icon-btn"
                >
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.full_name || 'User'}
                    className="w-full h-full rounded-xl object-cover"
                  />
                ) : (
                  <User className="w-5 h-5 text-gray-600 dark:text-slate-300" />
                )}
                </Link>
              </>
            ) : (
              <Link
                to="/login"
                className="mobile-btn mobile-btn-primary text-sm py-2 px-4"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>
    );
  }

  // Web Header (original)
  return (
    <header className="theme-header-banner sticky top-0 z-50 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center shrink-0 mr-6">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-white/80 transition-colors">
              <img src={EventHQLogo} alt="Event HQ" className="w-8 h-8 rounded-lg object-contain" />
              <span className="text-sm font-bold whitespace-nowrap">Podium HQ</span>
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/tournaments" className="text-white/80 hover:text-white font-medium transition-colors text-center">
              Tournaments
            </Link>
            <Link to="/leagues" className="text-white/80 hover:text-white font-medium transition-colors text-center">
              Leagues
            </Link>
            <Link to="/clinics" className="text-white/80 hover:text-white font-medium transition-colors text-center">
              Camps
            </Link>
            <Link to="/for-teams" className="text-white/80 hover:text-white font-medium transition-colors text-center">
              Teams
            </Link>
            {/* Live link hidden until feature is ready for public
            <Link to="/live" className="relative flex items-center gap-1.5 font-medium transition-colors text-center" style={{ color: 'var(--color-secondary)' }}>
              <Radio className={`w-4 h-4 ${liveStreamCount > 0 ? 'animate-pulse' : ''}`} />
              Live
              {liveStreamCount > 0 && (
                <span className="absolute -top-2 -right-4 min-w-[18px] h-[18px] flex items-center justify-center bg-rose-600 text-white text-[10px] font-bold rounded-full px-1">
                  {liveStreamCount}
                </span>
              )}
            </Link>
            */}
            {user && (
              <Link to="/dashboard" className="text-white/80 hover:text-white font-medium transition-colors text-center">
                My Dashboard
              </Link>
            )}
            {user && (
              <Link to="/feed" className="text-white/80 hover:text-white font-medium transition-colors text-center">
                Activity Feed
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {!user && (
              <Link
                to="/search"
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Search"
              >
                <Search className="w-5 h-5" />
              </Link>
            )}
            {user && <NotificationBell variant="dark" />}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                >
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.full_name || 'User'}
                      className="w-8 h-8 rounded-full object-cover border-2 border-white/30"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-white">
                    {profile?.full_name || 'User'}
                  </span>
                  <MoreVertical className="w-4 h-4 text-white/70 shrink-0" aria-hidden />
                </button>

                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col max-h-[calc(100dvh-4.5rem)] overflow-hidden"
                    role="menu"
                  >
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0">
                      {profile?.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.full_name || 'User'}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-600" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{profile?.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <Link
                      to="/search"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 border-b border-gray-100 shrink-0"
                    >
                      <Search className="w-4 h-4" />
                      Search
                    </Link>
                    <div className="user-menu-role-switcher px-4 py-3 border-b border-gray-100 shrink-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Role Selection</p>
                      <RoleSwitcher />
                    </div>
                    <div className="overflow-y-auto overflow-x-hidden min-h-0 flex-1 py-2 overscroll-contain">
                    <Link
                      to="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      My Dashboard
                    </Link>
                    <Link
                      to="/communications"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Inbox className="w-4 h-4 shrink-0" aria-hidden />
                      My Inbox
                    </Link>
                    <Link
                      to={`/users/${user.id}`}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>
                    <Link
                      to="/feed"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Newspaper className="w-4 h-4 shrink-0" aria-hidden />
                      Activity Feed
                    </Link>
                    <Link
                      to="/my-calendar"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <CalendarDays className="w-4 h-4" />
                      My Calendar
                    </Link>
                    {myTeamsMenuItems.length > 0 && (
                      <>
                        <button
                          onClick={() => setMyTeamsOpen(!myTeamsOpen)}
                          className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <span className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            My Teams
                          </span>
                          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${myTeamsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {myTeamsOpen && (
                          <div className="bg-gray-50">
                            {myTeamsMenuItems.map((team) => (
                              <button
                                key={team.id}
                                type="button"
                                onClick={() => handleMyTeamClick(team)}
                                className="flex items-center gap-2 pl-10 pr-4 py-2 text-sm text-gray-600 hover:bg-gray-100 w-full text-left"
                              >
                                {team.logoUrl ? (
                                  <img src={team.logoUrl} alt={team.name} className="w-5 h-5 rounded-full object-cover" />
                                ) : (
                                  <div className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center">
                                    <Users className="w-3 h-3 text-teal-600" />
                                  </div>
                                )}
                                <span className="truncate">{team.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {myEvents.length > 0 && (
                      <>
                        <button
                          onClick={() => setMyEventsOpen(!myEventsOpen)}
                          className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            My Events
                          </span>
                          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${myEventsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {myEventsOpen && (
                          <div className="bg-gray-50">
                            {myEvents.map((event) => (
                              <Link
                                key={event.id}
                                to={`/tournaments/${event.slug || event.id}`}
                                onClick={() => { setUserMenuOpen(false); setMyEventsOpen(false); }}
                                className="flex items-center gap-2 pl-10 pr-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                              >
                                <Trophy className="w-4 h-4 text-teal-600" />
                                <span className="truncate">{event.name}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <Link
                      to="/my-history"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Trophy className="w-4 h-4 text-amber-500" />
                      My Tournament History
                    </Link>
                    <Link
                      to="/teams"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Users className="w-4 h-4" />
                      Browse Teams
                    </Link>
                    <Link
                      to="/athletes"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User className="w-4 h-4" />
                      Browse Athletes
                    </Link>
                    {isCoach && (
                      <Link
                        to="/coach-fees"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <DollarSign className="w-4 h-4" />
                        Fee Collection
                      </Link>
                    )}
                    <Link
                      to="/video-vault"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Film className="w-4 h-4" />
                      Video Vault
                    </Link>
                    <Link
                      to="/notification-settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Bell className="w-4 h-4" />
                      Notification Settings
                    </Link>
                    <Link
                      to="/billing"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <CreditCard className="w-4 h-4" />
                      Billing
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings className="w-4 h-4" />
                      Account Settings
                    </Link>
                    {(isViewingAsOrganizer || profile?.role === 'admin' || profile?.role === 'organizer') && (
                      <>
                        <hr className="my-2" />
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-teal-600 hover:bg-teal-50"
                        >
                          <Settings className="w-4 h-4" />
                          Admin Panel
                        </Link>
                        <button
                          onClick={() => { generateMonetizationDoc(); setUserMenuOpen(false); }}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 w-full text-left"
                        >
                          <FileDown className="w-4 h-4" />
                          Monetization Plan (.docx)
                        </button>
                      </>
                    )}
                    <hr className="my-2" />
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                    <div className="mt-2 pt-3 pb-2 border-t border-gray-100 flex flex-col items-center gap-2">
                      <span className="text-xs text-gray-700 tracking-wide">Powered by Podium Nation</span>
                      <img src={PNDiamondLogo} alt="Podium Nation" className="w-8 h-8" />
                      <button
                        type="button"
                        onClick={() => setPodiumEcosystemOpen(!podiumEcosystemOpen)}
                        aria-expanded={podiumEcosystemOpen}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                      >
                        <span>More from Podium Nation</span>
                        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${podiumEcosystemOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {podiumEcosystemOpen && (
                      <div className="w-full bg-gray-50 border-t border-gray-100">
                        {PODIUM_ECOSYSTEM_LINKS.map(({ href, label }) => (
                          <a
                            key={href}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 pl-10 pr-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                          >
                            <Globe className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                            <span className="leading-snug">{label}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white/80 hover:text-white font-medium transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-white px-4 py-2 rounded-lg font-medium hover:bg-white/90 transition-colors"
                  style={{ color: 'var(--color-primary)' }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white hover:text-white/80"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-50 bg-white dark:bg-slate-900 overflow-y-auto">
          <div className="flex flex-col w-full">
            <Link
              to="/search"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
            >
              <Search className="w-5 h-5" />
              Search
            </Link>
            
            <Link
              to="/tournaments"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
            >
              <Trophy className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              Tournaments
            </Link>
            <Link
              to="/leagues"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
            >
              <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Leagues
            </Link>
            <Link
              to="/clinics"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
            >
              <GraduationCap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              Camps
            </Link>

            {/* Live link hidden until feature is ready for public
            <Link
              to="/live"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 w-full px-6 py-4 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
            >
              <Radio className={`w-5 h-5 ${liveStreamCount > 0 ? 'animate-pulse' : ''}`} />
              Live Streams
              {liveStreamCount > 0 && (
                <span className="ml-auto min-w-[22px] h-[22px] flex items-center justify-center bg-rose-600 text-white text-xs font-bold rounded-full px-1.5">
                  {liveStreamCount}
                </span>
              )}
            </Link>
            */}
            
            {user && (
              <>
                <div className="w-full border-b border-gray-100 dark:border-slate-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Role Selection</span>
                    <RoleSwitcherCompact />
                  </div>
                </div>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <LayoutDashboard className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  My Dashboard
                </Link>
                <Link
                  to="/communications"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <Inbox className="w-5 h-5 text-gray-500 dark:text-slate-400 shrink-0" aria-hidden />
                  My Inbox
                </Link>
                <Link
                  to={`/users/${user.id}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <User className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  My Profile
                </Link>
                <Link
                  to="/feed"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <Newspaper className="w-5 h-5 text-gray-500 dark:text-slate-400 shrink-0" aria-hidden />
                  Activity Feed
                </Link>
                <Link
                  to="/my-calendar"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <CalendarDays className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  My Calendar
                </Link>
                {myTeamsMenuItems.length > 0 && (
                  <div className="w-full border-b border-gray-100 dark:border-slate-800">
                    <button
                      onClick={() => setMyTeamsOpen(!myTeamsOpen)}
                      className="flex items-center justify-between w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 text-lg font-medium"
                    >
                      <span className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                        My Teams
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${myTeamsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {myTeamsOpen && (
                      <div className="bg-gray-50 dark:bg-slate-800/50">
                        {myTeamsMenuItems.map((team) => (
                          <button
                            key={team.id}
                            type="button"
                            onClick={() => handleMyTeamClick(team)}
                            className="flex items-center gap-3 w-full pl-14 pr-6 py-3 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 text-base font-medium text-left"
                          >
                            {team.logoUrl ? (
                              <img src={team.logoUrl} alt={team.name} className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                                <Users className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                              </div>
                            )}
                            <span className="truncate">{team.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {myEvents.length > 0 && (
                  <div className="w-full border-b border-gray-100 dark:border-slate-800">
                    <button
                      onClick={() => setMyEventsOpen(!myEventsOpen)}
                      className="flex items-center justify-between w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 text-lg font-medium"
                    >
                      <span className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                        My Events
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${myEventsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {myEventsOpen && (
                      <div className="bg-gray-50 dark:bg-slate-800/50">
                        {myEvents.map((event) => (
                          <Link
                            key={event.id}
                            to={`/tournaments/${event.slug || event.id}`}
                            onClick={() => { setMobileMenuOpen(false); setMyEventsOpen(false); }}
                            className="flex items-center gap-3 w-full pl-14 pr-6 py-3 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 text-base font-medium"
                          >
                            <Trophy className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                            <span className="truncate">{event.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Link
                  to="/my-history"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <Trophy className="w-5 h-5 text-amber-500 shrink-0" />
                  My Tournament History
                </Link>
              </>
            )}
            
            {user && (isViewingAsOrganizer || profile?.role === 'admin' || profile?.role === 'organizer') && (
              <>
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <Settings className="w-5 h-5" />
                  Admin Panel
                </Link>
                <button
                  onClick={() => { generateMonetizationDoc(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 w-full px-6 py-4 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border-b border-gray-100 dark:border-slate-800 text-lg font-medium text-left"
                >
                  <FileDown className="w-5 h-5" />
                  Monetization Plan (.docx)
                </button>
              </>
            )}
            
            {user ? (
              <>
                {isCoach && (
                  <Link
                    to="/coach-fees"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                  >
                    <DollarSign className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                    Fee Collection
                  </Link>
                )}
                <Link
                  to="/video-vault"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <Film className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  Video Vault
                </Link>
                <Link
                  to="/billing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <CreditCard className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  Billing
                </Link>
                <Link
                  to="/notification-settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-b border-gray-100 dark:border-slate-800 text-lg font-medium"
                >
                  <Bell className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  Notification Settings
                </Link>
                <button
                  onClick={() => {
                    handleSignOut();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 w-full px-6 py-4 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 border-b border-gray-100 dark:border-slate-800 text-lg font-medium text-left"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              </>
            ) : (
              <div className="p-6 space-y-3">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center w-full px-6 py-4 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-lg font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center w-full px-6 py-4 bg-teal-600 text-white hover:bg-teal-700 rounded-lg text-lg font-medium"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
